// ** React Imports
import { Fragment, useContext, useState, useEffect } from "react";

// ** Redux & Store & Actions
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";

// ** Custom Components
import Block from "components/layout/Block";

// ** Third Party Components
import { useTranslation } from "react-i18next";

// ** Custom Hooks - Ranking Selectors Render Functions
import useRankingSexSelector from "./hooks/useRankingSexSelector";

// ** Contexts
import deviceTypeContext from "contexts/deviceTypeContext";
import useRankingCountrySelector from "./hooks/useRankingCountrySelector";
import useRankingRegionSelector from "./hooks/useRankingRegionSelector";

// ** Utils & Helpers
import api from "helpers/ApiClient";

// #####################################################

const RankingFilters = ({ setFiltersOpen }) => {
	const { isPhone } = useContext(deviceTypeContext);

	const { t } = useTranslation(["countries"]);
	const params = useParams();
	const { country: countryUrl, region: regionUrl } = params || {};
	const userCountry = useSelector((state) => state.global.user.country);

	const [countriesList, setCountriesList] = useState([]);
	const [regionsList, setRegionsList] = useState([]);
	const [selectedCountry, setSelectedCountry] = useState(
		countryUrl || userCountry
	);
	const [selectedRegion, setSelectedRegion] = useState(null);
	const [filterCountry, setFilterCountry] = useState(null);

	// ** Filtr - Sex - 'Wyszukiwana Płeć'
	const { renderMultipleSexSelector } = useRankingSexSelector();

	// ** Filtr - Kraj
	const { renderCountrySelector } = useRankingCountrySelector({
		countriesList,
		setSelectedCountry,
		filterCountry,
	});

	// ** Filtr - Region
	const { renderRegionSelector } = useRankingRegionSelector({
		regionsList,
		selectedRegion,
	});

	const filters = [
		["sex", renderMultipleSexSelector],
		["country", renderCountrySelector],
		["region", renderRegionSelector],
	].filter((item) => Boolean(item));

	useEffect(() => {
		const country = countriesList.find(
			(item) =>
				item.url === selectedCountry || item.id === selectedCountry
		);
		setFilterCountry(country);
	}, [selectedCountry, countriesList]);

	useEffect(() => {
		if (regionsList) {
			const selectedRegionNew = regionsList.find(
				(item) => item?.url.toLowerCase() === regionUrl
			);
			setSelectedRegion(selectedRegionNew?.id);
		}
	}, [selectedCountry, countriesList, regionsList]);

	useEffect(() => {
		if (countriesList.length === 0) {
			api.get(`/statics/countries`)
				.then(({ data } = {}) => {
					if (data) {
						const finalCountries = data
							.map((item) => ({
								key: item.id,
								...item,
								content: t(item.id),
								text: t(item.id),
								value: item.id,
								withRegions: item.withRegions,
							}))
							.sort((a, b) => {
								if (a.text > b.text) {
									return 1;
								}
								return -1;
							});
						setCountriesList(finalCountries);
					} else {
						setCountriesList([]);
					}
				})
				.catch(() => {
					setCountriesList([]);
				});
		}
	}, []);

	useEffect(() => {
		if (filterCountry?.id && filterCountry?.withRegions) {
			api.get(`/statics/regions/country?country=${filterCountry.id}`)
				.then(({ data } = {}) => {
					if (data) {
						const finalRegions = Object.entries(data)?.map(
							([, item]) => ({
								key: item.id,
								id: item.id,
								name: item.name,
								name_filter: item.name_filter,
								url: item.url,
								content: item.name,
								text: item.name_filter,
								value: item.id,
								meta: { url: item.url },
							})
						);
						setRegionsList(finalRegions);
					} else {
						setRegionsList([]);
					}
				})
				.catch(() => {
					setRegionsList([]);
				});
		}
	}, [filterCountry?.id]);

	// #####################################################

	return (
		<Block
			mt={3}
			mb={2}
			flex
			fullWidth={isPhone}
			flexWrap
			alignEnd={!isPhone}
			alignCenter={!isPhone}
			column={isPhone}
			withRowGap
		>
			{filters.map(([key, renderFunction]) =>
				typeof renderFunction === "function" ? (
					<Fragment key={`selector-${key}`}>
						{renderFunction(setFiltersOpen)}
					</Fragment>
				) : null
			)}
		</Block>
	);
};

// #####################################################

export default RankingFilters;

// #####################################################
